<template>
    <div v-if="questionSpec.questionType === 'PlanTextQuestion'">
        <input-text
            v-if="questionSpec.size === 'SMALL'"
            class="w-full md:w-1/2 lg:w-1/3"
            :model-value="propertyValue"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
        <input-text
            v-if="questionSpec.size === 'MEDIUM'"
            class="w-full md:w-2/3 lg:w-1/2"
            :model-value="propertyValue"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
        <input-text
            v-if="questionSpec.size === 'LARGE'"
            class="w-full"
            :model-value="propertyValue"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
    </div>

    <div v-if="questionSpec.questionType === 'PlanParagraphQuestion'">
        <prime-textarea
            v-if="questionSpec.size === 'SMALL'"
            class="w-full md:w-1/2 lg:w-1/3"
            :model-value="propertyValue"
            rows="5"
            cols="30"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
        <prime-textarea
            v-if="questionSpec.size === 'MEDIUM'"
            class="w-full md:w-2/3 lg:w-1/2"
            :model-value="propertyValue"
            rows="5"
            cols="30"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
        <prime-textarea
            v-if="questionSpec.size === 'LARGE'"
            class="w-full"
            :model-value="propertyValue"
            rows="5"
            cols="30"
            :pt="{
                root: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateTextOrParagraph"
        />
    </div>

    <div v-if="questionType === 'PlanBooleanQuestion'">
        <checkbox
            :model-value="propertyValue"
            :binary="true"
            true-value="1"
            false-value="0"
            :pt="{
                input: {
                    class: showInvalidInput ? 'border-2 border-red-500' : ''
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="updateBoolean"
        />
        <label class="ml-2">{{ $t('yes') }}</label>
    </div>

    <div
        v-if="questionSpec.questionType === 'PlanNumberQuestion'"
        class="w-min"
    >
        <div
            v-if="questionSpec.numberConfig.pricing !== null"
            class="pt-2"
        >
            <span
                v-if="
                    questionSpec.numberConfig.pricing.feePricingTotal.isNotZero()
                "
                class="pl-2 text-gray-600"
            >
                +{{
                    questionSpec.numberConfig.pricing.feePricingTotal.formatCurrency()
                }}
            </span>
            <span
                v-if="
                    questionSpec.numberConfig.pricing.duesPricingTotal.isNotZero()
                "
                class="pl-2 text-gray-600"
            >
                +{{
                    formatRecurringTotal(
                        questionSpec.numberConfig.pricing.duesPricingTotal
                    )
                }}
            </span>
        </div>

        <input-number
            :model-value="
                isNaN(parseInt(propertyValue))
                    ? undefined
                    : parseInt(propertyValue)
            "
            :min="
                questionSpec.numberConfig.min === null
                    ? 0
                    : questionSpec.numberConfig.min
            "
            :max="
                questionSpec.numberConfig.max === null
                    ? undefined
                    : questionSpec.numberConfig.max
            "
            :increment-button-props="{ text: true }"
            :decrement-button-props="{ text: true }"
            input-class="w-12 text-center py-2 px-1 border-transparent ring-0 focus:outline-none focus:shadow-none hover:border-transparent dark:focus:shadow-none"
            class="border border-gray-300 focus-within:ring focus-within:ring-offset-1"
            decrement-button-class="order-first"
            show-buttons
            button-layout="horizontal"
            :pt="{
                root: {
                    class: showInvalidInput
                        ? 'border-2 border-red-500 w-auto inline-flex'
                        : 'w-auto inline-flex'
                }
            }"
            :pt-options="{ mergeSections: true, mergeProps: true }"
            @update:model-value="
                updateNumberQuestion($event, questionSpec.numberConfig.pricing)
            "
        />
    </div>

    <div v-if="questionSpec.questionType === 'PlanSingleChoiceQuestion'">
        <div
            v-if="questionSpec.questionInputType === 'RADIO_BUTTON_LIST'"
            class="flex flex-col flex-wrap gap-1"
        >
            <div
                v-for="option in planQuestionOptions"
                :key="option.id"
            >
                <div class="flex items-center">
                    <radio-button
                        :model-value="planQuestionOptionIds[0]"
                        :value="option.id"
                        :pt="{
                            box: {
                                class: showInvalidInput
                                    ? 'border-2 !border-red-500'
                                    : ''
                            }
                        }"
                        :input-id="createInputId(option.id)"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="
                            updateOptionsSingleChoice(option.id, option.pricing)
                        "
                    />
                    <label
                        :for="createInputId(option.id)"
                        class="ml-2"
                    >
                        {{ option.label }}
                        <template v-if="option.pricing !== undefined">
                            <span
                                v-if="
                                    option.pricing.feePricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    option.pricing.feePricingTotal.formatCurrency()
                                }}
                            </span>
                            <span
                                v-if="
                                    option.pricing.duesPricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    formatRecurringTotal(
                                        option.pricing.duesPricingTotal
                                    )
                                }}
                            </span>
                        </template>
                    </label>
                </div>
            </div>
        </div>
        <div
            v-if="questionSpec.questionInputType === 'DROPDOWN'"
            :class="
                questionSpec.size === 'SMALL'
                    ? 'w-full md:w-1/2 lg:w-1/3'
                    : questionSpec.size === 'MEDIUM'
                    ? 'w-full md:w-2/3 lg:w-1/2'
                    : questionSpec.size === 'LARGE'
                    ? 'w-full'
                    : ''
            "
        >
            <prime-dropdown
                :options="planQuestionOptions"
                :model-value="singleChoiceSelectedOption"
                option-label="label"
                class="!w-full"
                :show-clear="singleChoiceSelectedOption.length > 0"
                :pt="{
                    root: {
                        class: showInvalidInput ? 'border-2 border-red-500' : ''
                    }
                }"
                :pt-options="{ mergeSections: true, mergeProps: true }"
                @update:model-value="handleDropdownSingleChoice"
            >
                <template #value="slotProps">
                    <template v-if="slotProps.value[0] === undefined">
                        {{ $t('purchaseContract.selectAnOption') }}
                    </template>
                    <template v-else>
                        <div>
                            {{ slotProps.value[0].label }}
                            <template
                                v-if="slotProps.value[0].pricing !== undefined"
                            >
                                <span
                                    v-if="
                                        slotProps.value[0].pricing.feePricingTotal.isNotZero()
                                    "
                                    class="pl-2 text-gray-600"
                                >
                                    +{{
                                        slotProps.value[0].pricing.feePricingTotal.formatCurrency()
                                    }}
                                </span>
                                <span
                                    v-if="
                                        slotProps.value[0].pricing.duesPricingTotal.isNotZero()
                                    "
                                    class="pl-2 text-gray-600"
                                >
                                    +{{
                                        formatRecurringTotal(
                                            slotProps.value[0].pricing
                                                .duesPricingTotal
                                        )
                                    }}
                                </span>
                            </template>
                        </div>
                    </template>
                </template>

                <template #option="slotProps">
                    <div>
                        {{ slotProps.option.label }}
                        <template v-if="slotProps.option.pricing !== undefined">
                            <span
                                v-if="
                                    slotProps.option.pricing.feePricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    slotProps.option.pricing.feePricingTotal.formatCurrency()
                                }}
                            </span>
                            <span
                                v-if="
                                    slotProps.option.pricing.duesPricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    formatRecurringTotal(
                                        slotProps.option.pricing
                                            .duesPricingTotal
                                    )
                                }}
                            </span>
                        </template>
                    </div>
                </template>
            </prime-dropdown>
        </div>
    </div>

    <div v-if="questionType === 'PlanMultipleChoiceQuestion'">
        <div class="flex flex-col flex-wrap gap-1">
            <div
                v-for="option in planQuestionOptions"
                :key="option.id"
            >
                <div class="flex items-center">
                    <checkbox
                        :input-id="createInputId(option.id)"
                        :model-value="planQuestionOptionIds.includes(option.id)"
                        :binary="true"
                        :pt="{
                            box: {
                                class: showInvalidInput
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                        @update:model-value="
                            updateOptionsMultipleChoice(
                                option.id,
                                option.pricing
                            )
                        "
                    />
                    <label
                        :for="createInputId(option.id)"
                        class="ml-2"
                    >
                        {{ option.label }}
                        <template v-if="option.pricing !== undefined">
                            <span
                                v-if="
                                    option.pricing.feePricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    option.pricing.feePricingTotal.formatCurrency()
                                }}
                            </span>
                            <span
                                v-if="
                                    option.pricing.duesPricingTotal.isNotZero()
                                "
                                class="pl-2 text-gray-600"
                            >
                                +{{
                                    formatRecurringTotal(
                                        option.pricing.duesPricingTotal
                                    )
                                }}
                            </span>
                        </template>
                    </label>
                </div>
            </div>
        </div>
    </div>

    <div
        v-if="questionType === 'PlanPhoneNumberQuestion'"
        class="md:w-1/2"
    >
        <phone-number-input
            :placeholder="$t('customerInformation.phoneNumber')"
            :model-value="phoneNumber"
            :initial-value="propertyValue"
            :autofocus="false"
            :invalid="invalidMobilePhoneMessage !== undefined"
            @update:model-value="updatePhoneNumber"
        />
        <div class="text-red-600 text-sm">
            {{ invalidMobilePhoneMessage || '&nbsp;' }}
        </div>
    </div>

    <div v-if="questionType === 'PlanEmailQuestion'">
        <span class="p-input-icon-left w-full">
            <i class="pi pi-envelope"></i>
            <input-text
                id="email"
                v-model="email"
                v-bind="emailAttrs"
                class="w-full"
                inputmode="email"
                type="email"
                :placeholder="$t('customerInformation.emailAddress')"
                :pt="{
                    root: {
                        class: showInvalidInput ? 'border-2 border-red-500' : ''
                    }
                }"
                :pt-options="{ mergeSections: true, mergeProps: true }"
            />
        </span>
        <span
            id="email-error"
            class="text-small text-red-600"
            >{{ errors.email || '&nbsp;' }}</span
        >
    </div>
</template>

<script setup lang="ts">
import PrimeTextarea from 'primevue/textarea'
import InputNumber from 'primevue/inputnumber'
import RadioButton from 'primevue/radiobutton'
import InputText from 'primevue/inputtext'
import checkbox from 'primevue/checkbox'
import { computed, onMounted, ref, watch } from 'vue'
import type {
    ContractQuestionFormData,
    PlanQuestionOptionSpec,
    QuestionPricingConfig,
    QuestionValue
} from '~/composables/planQuestionFormData'
import type { PhoneObject } from '~/components/Input/PhoneNumber.vue'
import PhoneNumberInput from '~/components/Input/PhoneNumber.vue'
import PrimeDropdown from 'primevue/dropdown'
import { formatRecurringTotalString } from '~/composables/contract-question-formatting'
import type { BillingInterval } from '~/resources/graphql/graphql'
import type { Decimal } from '~/composables/support/decimal'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    question: ContractQuestionFormData
    billingInterval: BillingInterval | null
    // showInvalidQuestions means to highlight the input in red if the input is invalid
    showInvalidQuestions: boolean
    uniqueKey: string | undefined
}>()

const emit = defineEmits<{
    (e: 'update:value', payload: QuestionValue): void
    (e: 'updatePricing'): void
}>()

const { t } = useI18n()
const questionSpec = computed(() => props.question.planQuestion)
const questionType = computed(() => props.question.planQuestion.questionType)
const propertyValue = computed(() => props.question.propertyValue)
const planQuestionOptions = computed(() => props.question.planQuestionOptions)
const planQuestionOptionIds = computed(
    () => props.question.planQuestionOptionIds
)
const required = computed(() => props.question.required)
const invalid = computed(() => props.question.invalid)

onMounted(() => {
    // Handle loading existing email data
    if (questionType.value === 'PlanEmailQuestion') {
        email.value = propertyValue.value
    }
})

// Whether we should highlight this input as invalid
const showInvalidInput = computed(() => {
    return props.showInvalidQuestions && invalid.value
})

function updateTextOrParagraph(value: string) {
    emit('update:value', {
        propertyValue: value,
        planQuestionOptionIds: [],
        invalid: required.value && value === ''
    })
}

function updateBoolean(value: string) {
    emit('update:value', {
        propertyValue: value,
        planQuestionOptionIds: [],
        invalid: required.value && value === '0'
    })
}

function updateNumberQuestion(
    value: number | null,
    pricing: QuestionPricingConfig | null
) {
    if (value === null) {
        emit('update:value', {
            propertyValue: '',
            planQuestionOptionIds: [],
            invalid: true
        })
        return
    }

    emit('update:value', {
        propertyValue: String(value),
        planQuestionOptionIds: [],
        invalid: false
    })
    if (
        pricing !== null &&
        (pricing.feePricingTotal.isNotZero() ||
            pricing.duesPricingTotal.isNotZero())
    ) {
        emit('updatePricing')
    }
}

const singleChoiceSelectedOption = computed(() => {
    return planQuestionOptions.value.filter(
        option => option.id === planQuestionOptionIds.value[0]
    )
})
function updateOptionsSingleChoice(
    optionId: string,
    pricing: QuestionPricingConfig | undefined
) {
    let hadPricing = false
    if (planQuestionOptionIds.value.length > 0) {
        const previousPricingOption = planQuestionOptions.value.find(option => {
            return (
                option.pricing !== undefined &&
                (option.pricing.feePricingTotal.isNotZero() ||
                    option.pricing.duesPricingTotal.isNotZero())
            )
        })
        if (previousPricingOption !== undefined) {
            hadPricing = true
        }
    }

    const selectedOptionIds = [optionId]
    emit('update:value', {
        propertyValue: planQuestionOptions.value
            .filter(value => selectedOptionIds.includes(value.id))
            .map(value => value.label)
            .join(', '),
        planQuestionOptionIds: selectedOptionIds,
        invalid: false
    })

    if (
        hadPricing ||
        (pricing !== undefined &&
            (pricing.feePricingTotal.isNotZero() ||
                pricing.duesPricingTotal.isNotZero()))
    ) {
        emit('updatePricing')
    }
}

function updateOptionsMultipleChoice(
    optionId: string,
    pricing: QuestionPricingConfig | undefined
) {
    let hadPricing = false
    if (planQuestionOptionIds.value.length > 0) {
        const previousPricingOption = planQuestionOptions.value.find(option => {
            return (
                option.pricing !== undefined &&
                (option.pricing.feePricingTotal.isNotZero() ||
                    option.pricing.duesPricingTotal.isNotZero())
            )
        })
        if (previousPricingOption !== undefined) {
            hadPricing = true
        }
    }

    let selectedOptionIds = planQuestionOptionIds.value
    // if in the array remove it
    if (selectedOptionIds.includes(optionId)) {
        selectedOptionIds = selectedOptionIds.filter(value => {
            return value !== optionId
        })
    } else {
        selectedOptionIds.push(optionId)
    }
    emit('update:value', {
        propertyValue: planQuestionOptions.value
            .filter(value => selectedOptionIds.includes(value.id))
            .map(value => value.label)
            .join(', '),
        planQuestionOptionIds: selectedOptionIds,
        invalid: selectedOptionIds.length === 0 && required.value
    })
    if (
        hadPricing ||
        (pricing !== undefined &&
            (pricing.feePricingTotal.isNotZero() ||
                pricing.duesPricingTotal.isNotZero()))
    ) {
        emit('updatePricing')
    }
}

function handleDropdownSingleChoice(option: PlanQuestionOptionSpec | null) {
    if (option === null) {
        let hadPricing = false
        if (planQuestionOptionIds.value.length > 0) {
            const previousPricingOption = planQuestionOptions.value.find(
                option => {
                    return (
                        option.pricing !== undefined &&
                        (option.pricing.feePricingTotal.isNotZero() ||
                            option.pricing.duesPricingTotal.isNotZero())
                    )
                }
            )
            if (previousPricingOption !== undefined) {
                hadPricing = true
            }
        }

        emit('update:value', {
            propertyValue: '',
            planQuestionOptionIds: [],
            invalid: required.value
        })

        if (hadPricing) {
            emit('updatePricing')
        }
    } else {
        updateOptionsSingleChoice(option.id, option.pricing)
    }
}

const phoneNumber = ref<PhoneObject | undefined>()
function updatePhoneNumber(value: PhoneObject | undefined) {
    phoneNumber.value = value
    // If we have a valid phone number, we emit that
    if (value !== undefined && value.valid === true) {
        emit('update:value', {
            propertyValue: value.number,
            planQuestionOptionIds: [],
            invalid: false
        })
    } else if (value === undefined || value.valid === undefined) {
        // Else if our value is undefined
        // Emit empty string and valid depending on if this is required question
        emit('update:value', {
            propertyValue: '',
            planQuestionOptionIds: [],
            invalid: required.value
        })
    } else {
        // Else we have an invalid value - emit empty string and invalid input
        emit('update:value', {
            propertyValue: '',
            planQuestionOptionIds: [],
            invalid: true
        })
    }
}

function formatRecurringTotal(price: Decimal) {
    if (props.billingInterval === null) {
        throw new Error(
            'Configuration error. Attempting to add dues pricing for non-recurring contract.'
        )
    }
    return formatRecurringTotalString(price, props.billingInterval)
}

const { errors, defineField } = useForm({
    validationSchema: object({
        email: string().email(t('customerInformation.invalidEmailWarning'))
    })
})

const [email, emailAttrs] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

// Validate on blur so this code runs on blur if there are changes
watch(errors, newValue => {
    // If we have errors we emit empty string and invalid input
    if (newValue.email) {
        emit('update:value', {
            propertyValue: '',
            planQuestionOptionIds: [],
            invalid: true
        })
    } else {
        // Else emit our value
        const propertyValue = email.value
        emit('update:value', {
            propertyValue: propertyValue,
            planQuestionOptionIds: [],
            invalid: required.value && propertyValue === ''
        })
    }
})

const invalidMobilePhoneMessage = computed(() => {
    if (
        (phoneNumber.value !== undefined &&
            phoneNumber.value.number !== undefined &&
            !phoneNumber.value.valid) ||
        showInvalidInput.value
    ) {
        return t('customerInformation.invalidMobilePhoneWarning')
    } else {
        return undefined
    }
})

function createInputId(optionId: string) {
    if (props.uniqueKey !== undefined) {
        return `option_id_${optionId}_${props.uniqueKey}`
    }

    return `option_id_${optionId}`
}
</script>
